import { Alert, Button, DatePicker, Input, Modal } from "@arco-design/web-react";

import { FINANCE_PAYMENT_REQUEST_ATTACHMENT_ACCEPT } from "../config";
import { useFinancePaymentRecordForm, useFinancePaymentVoidForm } from "../runtime/useFinancePaymentRecord";
import { formatFinancePaymentRequestAmount } from "../service/financePaymentRequestService";
import { Field } from "./FinancePaymentRequestFormModal";
import "./financePaymentRequest.css";

type RecordProps = {
  requestId: string;
  /** 请款金额，付款金额就是它。 */
  amount: string;
  onClose: () => void;
};

/** 登记已付：本方付款账户、付款日期、银行流水号、付款回单必填，备注选填；付款金额等于请款金额、不能改。 */
export function FinancePaymentRecordModal({ requestId, amount, onClose }: RecordProps) {
  const form = useFinancePaymentRecordForm(requestId);
  const { draft } = form;
  return (
    <Modal
      title="登记已付"
      visible
      style={{ width: 640 }}
      maskClosable={!form.uploading && !form.submitting}
      onCancel={() => { if (!form.submitting) onClose(); }}
      unmountOnExit
      footer={
        <div className="finance-payment-request-footer">
          <Button onClick={onClose} disabled={form.submitting}>取消</Button>
          <Button
            type="primary"
            loading={form.submitting}
            disabled={form.uploading}
            onClick={() => void form.submit().then((done) => { if (done) onClose(); })}
          >
            提交登记
          </Button>
        </div>
      }
    >
      <div className="finance-payment-request-form">
        <Field label="付款金额">
          <span className="finance-payment-request-money">{`${formatFinancePaymentRequestAmount(amount)}（等于请款金额，不能改）`}</span>
        </Field>
        <Field label="本方付款账户">
          <Input
            aria-label="本方付款账户"
            maxLength={300}
            placeholder="从哪个账户付的，例如开户行和账号尾号"
            value={draft.payerAccount}
            onChange={(value) => form.setField("payerAccount", value)}
          />
        </Field>
        <Field label="付款日期">
          <DatePicker
            value={draft.paymentDate || undefined}
            inputProps={{ "aria-label": "付款日期" }}
            onChange={(value) => form.setField("paymentDate", value ?? "")}
          />
        </Field>
        <Field label="银行流水号">
          <Input
            aria-label="银行流水号"
            maxLength={100}
            value={draft.bankSerialNo}
            onChange={(value) => form.setField("bankSerialNo", value)}
          />
        </Field>
        <Field label="付款回单（PDF、PNG、JPEG，单个 20 MB，至少 1 个）">
          <label className="finance-payment-request-file-button">
            选择付款回单
            <input
              type="file"
              aria-label="选择付款回单"
              accept={FINANCE_PAYMENT_REQUEST_ATTACHMENT_ACCEPT}
              disabled={form.uploading}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void form.upload(file);
              }}
            />
          </label>
          {form.uploading ? <span className="finance-payment-request-muted">正在上传…</span> : null}
          {form.uploadError ? <span className="finance-payment-request-error">{form.uploadError}</span> : null}
          {draft.attachments.length > 0 ? (
            <ul className="finance-payment-request-files">
              {draft.attachments.map((item) => (
                <li key={item.attachmentId}>
                  <span>{item.fileName}</span>
                  <Button type="text" size="mini" onClick={() => form.removeAttachment(item.attachmentId)}>移除</Button>
                </li>
              ))}
            </ul>
          ) : null}
        </Field>
        <Field label="备注（选填）">
          <Input.TextArea aria-label="备注" maxLength={500} value={draft.remark} onChange={(value) => form.setField("remark", value)} />
        </Field>
        {form.errors.length > 0 ? (
          <Alert
            type="error"
            content={<ul className="finance-payment-request-error-list">{form.errors.map((item) => <li key={item}>{item}</li>)}</ul>}
          />
        ) : null}
        {form.serverError ? <Alert type="error" content={form.serverError} /> : null}
      </div>
    </Modal>
  );
}

type VoidProps = {
  requestId: string;
  paymentId: string;
  onClose: () => void;
};

/** 作废这次登记：必填原因；作废后这张请款回到已通过，可以重新登记，作废的记录留着。 */
export function FinancePaymentVoidModal({ requestId, paymentId, onClose }: VoidProps) {
  const form = useFinancePaymentVoidForm(requestId, paymentId);
  return (
    <Modal
      title="作废这次登记"
      visible
      style={{ width: 520 }}
      maskClosable={!form.submitting}
      onCancel={() => { if (!form.submitting) onClose(); }}
      unmountOnExit
      footer={
        <div className="finance-payment-request-footer">
          <Button onClick={onClose} disabled={form.submitting}>取消</Button>
          <Button
            type="primary"
            status="danger"
            loading={form.submitting}
            onClick={() => void form.submit().then((done) => { if (done) onClose(); })}
          >
            确认作废
          </Button>
        </div>
      }
    >
      <div className="finance-payment-request-form">
        <span className="finance-payment-request-muted">作废后这张请款回到「已通过」，可以重新登记；这次登记和它的付款回单都留着。</span>
        <Field label="作废原因">
          <Input.TextArea aria-label="作废原因" maxLength={500} value={form.reason} onChange={form.setReason} />
        </Field>
        {form.error ? <Alert type="error" content={form.error} /> : null}
      </div>
    </Modal>
  );
}
